import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { grey } from "designSystem/styles/color";
import { useSyncLocalStore } from "designSystem/hooks";
import { Box, FlexColumn, FlexRowCenter, FormControlLabel, GridColumn, OverflowText, SearchTextField, SingleSelect, Switch, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { APGroup } from "./attributePickerService";
import AttributePickerStore, { AttributePickerStoreContext, useAttributePickerStore } from "./AttributePickerStore";

interface AttributePickerGroupProps {
	group: APGroup;
}

const AttributePickerToolbar = observer(() => {
	const {
		depthOption,
		depthOptions,
		domainClassOption,
		domainClassOptions,
		expand,
		onChangeDepth,
		onChangeDomainClass,
		onChangeSearch,
		onExpand,
		searchText,
	} = useAttributePickerStore();

	return (
		<Box
			display="flex"
			gap={3}
		>
			<FormControlLabel
				control={(
					<SingleSelect
						inline
						onChange={onChangeDomainClass}
						options={domainClassOptions}
						placeHolder="Select class..."
						sx={{
							width: 250,
						}}
						value={domainClassOption}
					/>
				)}
				label="Class"
				labelPlacement="start"
			/>
			<FormControlLabel
				control={(
					<SingleSelect
						inline
						onChange={onChangeDepth}
						options={depthOptions}
						value={depthOption}
					/>
				)}
				label="Depth"
				labelPlacement="start"
			/>
			<FormControlLabel
				control={(
					<SearchTextField
						onChange={onChangeSearch}
						placeholder="Type keyword to filter"
						selectOnFocus
						value={searchText}
					/>
				)}
				label="Search"
				labelPlacement="start"
			/>
			<FormControlLabel
				control={(
					<Switch
						checked={expand}
						onChange={onExpand}
					/>
				)}
				label="Expand"
				labelPlacement="end"
			/>
		</Box>
	);
});

const AttributePickerGroup: FunctionComponent<AttributePickerGroupProps> = observer(({ group }) => {
	const {
		getGroupAttributes,
		isGroupExpanded,
		toggleGroupCollapsed,
	} = useAttributePickerStore();
	const expanded = isGroupExpanded(group);
	const attributes = getGroupAttributes(group);

	return (
		<Box>
			<FlexRowCenter
				aria-label={expanded ? "collapse" : "expand"}
				gap={2}
				height={32}
				onClick={() => toggleGroupCollapsed(group)}
				sx={{
					cursor: "pointer",
				}}
			>
				{expanded ? <ExpandLess /> : <ExpandMore />}
				<Text
					flexShrink={0}
					semiBold
					text={group.name}
				/>
				<OverflowText
					color={grey[700]}
					text={group.info}
				/>
			</FlexRowCenter>
			<Collapse in={expanded} timeout="auto">
				<GridColumn spacing={2} spacingAroundX={8}>
					{
						attributes.map(({ id, name }) => (
							<OverflowText
								key={id}
								text={name}
							/>
						))
					}
				</GridColumn>
			</Collapse>
		</Box>
	);
});

const AttributePickerContent = observer(() => {
	const { groups } = useAttributePickerStore();

	return (
		<FlexColumn
			gap={1}
			width={800}
		>
			{
				groups.map((group) => (
					<AttributePickerGroup
						group={group}
						key={group.id}
					/>
				))
			}
		</FlexColumn>
	);
});

const AttributePickerView = () => {
	const loc = useLocale();
	const store = useSyncLocalStore(() => new AttributePickerStore(loc));

	return (
		<AttributePickerStoreContext.Provider value={store}>
			<GridColumn
				spacing={3}
				spacingAroundX={4}
				spacingAroundY={3}
			>
				<AttributePickerToolbar />
				<AttributePickerContent />
			</GridColumn>
		</AttributePickerStoreContext.Provider>
	);
};

export default AttributePickerView;
